/**
 * useUpdateIncomingOrder.js
 *
 * Custom hook to update an incoming supplier order.
 * - Declarative / callback-free
 * - Race-condition and abort-safe
 * - Optimistic-UI friendly (explicit state control)
 */

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_INCOMING_ORDER } from "../../../graphql/mutations/IncomingOrdersContext";

/**
 * Extracts update result from the GraphQL mutation response.
 * Returns null if data is not available.
 *
 * @param {Object} data - Apollo mutation result
 * @returns {Object|null}
 */
const extractResult = (data) =>
  data?.incomingOrders?.updateSupplierOrder || null;

/**
 * useUpdateIncomingOrder
 *
 * Custom hook to update an incoming supplier order.
 *
 * @returns {Object} - Contains:
 *   - updateIncomingOrder: Function to trigger update
 *   - updateResult: Mutation result (or null)
 *   - updateLoading: Loading state
 *   - updateError: Error object (or null)
 */
const useUpdateIncomingOrder = () => {
  // AbortController to cancel in-flight mutations
  const abortController = useRef(new AbortController());

  // Local state
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetUpdateResult = () => {
    setResult(null);
  };

  /**
   * Abort any ongoing request and create a new AbortController
   */
  const abortRequest = () => {
    abortController.current.abort();
    abortController.current = new AbortController();
  };

  /**
   * Abort request when component unmounts
   */
  useEffect(() => {
    return () => {
      abortRequest();
    };
  }, []);

  /**
   * Apollo useMutation for UPDATE_INCOMING_ORDER
   */
  const [runMutation, { data, error: mutationError, loading: mutationLoading, called }] =
    useMutation(UPDATE_INCOMING_ORDER, {
      errorPolicy: "none",
    });

  /**
   * Sync Apollo state with local state
   */
  useEffect(() => {
    if (!called) return;

    // Ignore abort errors
    if (mutationError?.name === "AbortError") return;

    setLoading(mutationLoading);

    const haveData = !mutationLoading && data;
    const extractedResult = haveData ? extractResult(data) : null;

    if (haveData && !extractedResult) {
      console.warn("Update mutation completed without a valid result\n");
      setError(new Error("No update result returned."));
      setResult(null);
    } else {
      setError(mutationError || null);
      setResult(extractedResult);
    }
  }, [data, mutationError, mutationLoading, called]);

  /**
   * Update incoming supplier order on-demand
   *
   * @param {number|string} supplierOrderId - Supplier order ID
   * @param {Object} patch - OrderPatchInput
   * @returns {Promise} - Apollo mutation promise
   */
  const updateIncomingOrder = async (supplierOrderId, patch) => {
    if (!Boolean(supplierOrderId)) {
      console.warn(`updateIncomingOrder called with invalid id: ${supplierOrderId}\n`);
      return;
    }

    const numericId =
      typeof supplierOrderId === "string"
        ? parseInt(supplierOrderId, 10)
        : supplierOrderId;

    if (isNaN(numericId)) {
      console.warn(`updateIncomingOrder called with invalid id: ${supplierOrderId}\n`);
      return;
    }

    abortRequest(); // Cancel previous mutation

    setResult(null);   // Reset previous result
    setLoading(true);  // Enter loading state
    setError(null);    // Reset error

    return runMutation({
      context: {
        fetchOptions: {
          signal: abortController.current.signal,
        },
      },
      variables: {
        supplierOrderId: numericId,
        patch,
      },
    });
  };

  return {
    updateIncomingOrder,
    updateResult: result,
    updateLoading: loading,
    updateError: error,
    resetUpdateResult, 
  };
};

export default useUpdateIncomingOrder;
